import React, { useState, useEffect , useRef, useCallback } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Box, 
    Typography, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    CircularProgress, 
    Alert,
    Snackbar,
    Collapse,
    Card,
    CardContent,
    Divider,
    IconButton,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,     
    Autocomplete,
    Checkbox,
} from '@mui/material';
import { styled } from '@mui/material/styles';

dayjs.extend(relativeTime);

const StyledPaper = styled(Paper)(({ theme }) => ({
    border: '1px solid #e0e0e0',
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
    boxShadow: 'none',
}));

const StyledTableRow = styled(TableRow)(({ theme, index }) => ({
    '& > td': {
        borderBottom: 'none',
    },
    backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#ffffff', 
}));

const getRegistrationType = (registrationNumber) => {
  if (!registrationNumber) return 'DOT/MC';
  const numStr = String(registrationNumber).toUpperCase();

  if (numStr.includes('MC-') || numStr.startsWith('MC')) {
    return 'MC';
  }
  return 'DOT';
};

const DetailSection = ({ title, children }) => (
    <Box sx={{ mb: 2 }}>
       <Typography
               variant="subtitle2"
               sx={{
                 fontWeight: "bold",
                 fontSize: "0.9rem",
                 color: "#11224d",
                 mb: 0.5,
                 borderBottom: '1px solid #ddd',
                 pb: 0.5,
               }}
        >
            {title}
        </Typography>
        {children}
    </Box>
);



const filterOptions = (options, state) => {
  const inputValue = state.inputValue.toLowerCase().trim();


  if (inputValue === "") {
    return []; 
  }

  return options.filter((option) => {
    const companyName = option.company_name.toLowerCase();
    const dotNumber = String(option.dot_number).toLowerCase();
    return companyName.startsWith(inputValue) || dotNumber.startsWith(inputValue);
  });
};

const HistoryRow = ({ load, idx, onDelete, carriers = [], onAssignCarrier, onOpenAssignDialog, selectionMode, isSelected, onSelect }) => {
    const [open, setOpen] = useState(false);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('MM/DD/YYYY');
    };

    const handleRowClick = (e) => {
        if (selectionMode) {
            onSelect(load.id);
        } else {
            setOpen(!open);
        }
    };
    const loadDetails = load.details || {};

    const assignedCarrier = load.carrier_id 
        ? carriers.find(c => c.id === load.carrier_id) 
        : null;
 


    return (
        <>
            <StyledTableRow 
                index={idx} 
                hover 
                onClick={handleRowClick} 
                sx={{ cursor: 'pointer' }}
            >
                <TableCell padding="checkbox" sx={{ borderBottom: open ? 'none' : null, py: 0.5 }}>
                    {selectionMode ? (
                        <Checkbox checked={isSelected} onClick={e => e.stopPropagation()} onChange={() => onSelect(load.id)} />
                    ) : (
                        <IconButton size="small" sx={{ p: 0, color: 'primary.main' }} onClick={(e) => { e.stopPropagation(); setOpen(!open); }}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem',py: 0.7}}>
                    {formatDate(load.pickupDate)}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.7}}>{load.origin}</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.7}}>{load.destination}</TableCell>
                <TableCell align="center" sx={{ fontSize: '0.75rem', py: 0.7 }}>{load.equipment}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: '700', py: 0.7, textAlign: 'center' }}>
  {load.offerRate && Number(load.offerRate) > 0
    ? (load.rateType === 'Flat'
        ? `$${Number(load.offerRate).toFixed(0)}`
        : `$${Number(load.offerRate).toFixed(0)}/mi`)
    : '-'}
</TableCell>

                <TableCell sx={{ py: 0.7, textAlign: 'center' }}>
    {load.carrier_id ? (
   
        <Typography
            variant="body2"
            sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#2e7d32',
                textAlign: 'center',
                display: 'inline-block'
            }}
        >
            {assignedCarrier ? (
                <>
                    {assignedCarrier.company_name}
                    {assignedCarrier.dot_number && (
                        <> ({getRegistrationType(assignedCarrier.dot_number)}: {assignedCarrier.dot_number})</>
                    )}
                </>
            ) : (
                'Carrier Assigned'
            )}
        </Typography>
    ) : (
        <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: '200px' }}>
            <Autocomplete
                options={carriers}
                getOptionLabel={(option) => `${option.company_name} (DOT ${option.dot_number})`}
                filterOptions={filterOptions}
                size="small"
                sx={{ minWidth: 200, maxWidth: 250 }}
                onClick={(e) => e.stopPropagation()}
                onChange={async (event, newValue) => {
                    if (!newValue) return;
                    onOpenAssignDialog(load.id, newValue);
                }}
                renderInput={(params) => (
                    <TextField 
                        {...params} 
                        label="Search DOT / Name" 
                        variant="outlined"
                        sx={{ 
                            fontSize: '0.75rem', 
                            '.MuiInputBase-root': { fontSize: '0.75rem', p: 1, },
                            '.MuiInputLabel-root': { fontSize: '0.75rem' }
                        }}
                    />
                )}
            />
        </Box>
    )}
</TableCell>


                <TableCell align="center" padding="checkbox" sx={{ py: 0.5 }}>
                    {!selectionMode && (
                        <IconButton size="small" color="error" onClick={e => { e.stopPropagation(); onDelete(load.id); }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </TableCell>
            </StyledTableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
    <Card
        sx={{
            border: "1px solid #f0f0f0", 
            borderRadius: 1, 
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            backgroundColor: "#ffffff",
        }}
    >
        <CardContent sx={{ p: 1.5 }}>
    
            <Typography
                variant="body2"
                sx={{
                    fontWeight: "bold",
                    mb: 1,
                    fontSize: "1rem", 
                    color: "#11224d", 
                }}
            >
                {load.origin} → {load.destination} ({load.tripMiles ? Math.round(Number(load.tripMiles)) : "N/A"} mi)
            </Typography>
            <Divider sx={{ my: 1 }} /> 

    
            <Box sx={{ display: "flex", width: "100%", mt: 1 }}>
          
                <Box sx={{ flex: '3 1 0%', pr: 3 }}>
                    <DetailSection title="Trip">
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>

                    <Box sx={{ mr: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
       
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                border: '2px solid #1976d2',
                                backgroundColor: 'white',
                            }}
                        />
                    
                        <Box
                            sx={{
                                height: 49,
                                borderLeft: '1px dashed #bdbdbd',
                            }}
                        />
                  
                        <Box
                            sx={{
                                width: 9,
                                height: 9,
                                borderRadius: '50%',
                                backgroundColor: '#1976d2',
                            }}
                        />
                    </Box>
                                
                         
                            <Box>
                                <Typography variant="caption" sx={{ display: "block" }}>
                                    <span style={{ fontWeight: "bold" }}>{load.origin}</span> 
                                    
                                </Typography>
                                <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                                {formatDate(load.pickupDate)} - (Pickup Time: {load.pickupTime || "N/A"})

                               </Typography>

                                <Typography  variant="caption" sx={{ display: "block", mt: 2  }}>
                                    <span style={{ fontWeight: "bold" }}> {load.destination} </span> 
                                  - (Dropoff Time: {load.dockTime || "N/A"})
                                
                                </Typography>
                                 
                            </Box>
                        </Box>
                  
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="caption" sx={{ display: "block" }}>
                                <span style={{ fontWeight: 'bold' }}>Equipment: </span>{load.equipment || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block" }}>
                                <span style={{ fontWeight: 'bold' }}>Load: </span>{load.fullOrPartial || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block" }}>
                                <span style={{ fontWeight: 'bold' }}>Feet: </span>{load.length ? `${load.length} ft` : 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block" }}>
                                <span style={{ fontWeight: 'bold' }}>Weight: </span>{load.weight ? `${load.weight} lbs` : 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block" }}>
                                <span style={{ fontWeight: 'bold' }}>Commodity: </span>{load.commodity || 'N/A'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block" }}>
                                <span style={{ fontWeight: 'bold' }}>Reference ID: </span>{load.referenceId || 'N/A'}
                            </Typography>
                        </Box>
                    </DetailSection>
                </Box>

             
                <Box sx={{ flex: '5 1 0%' }}>
              
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  
                        <Box sx={{ flex: '1 1 50%', pr: 2 }}>
                            <DetailSection title="Rate & Payment">
                                <Typography variant="caption" sx={{ display: "block" }}>
                                    <span style={{ fontWeight: 'bold' }}>Offer Rate </span> ({load.rateType || 'N/A'}): ${Number(load.offerRate || 0).toFixed(0)}
                                </Typography>
                                <Typography variant="caption" sx={{ display: "block" }}>
                                    <span style={{ fontWeight: 'bold' }}>Rate / Mile: </span> ${load.rate !== null && load.rate !== undefined ? Number(load.rate).toFixed(0) : '-'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: "block" }}>
                                    <span style={{ fontWeight: 'bold' }}>Trip Miles: </span>{load.tripMiles || 'N/A'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: "block" }}>
                                    <span style={{ fontWeight: 'bold' }}>Total Rate: </span> ${load.totalRate !== null && load.totalRate !== undefined ? Number(load.totalRate).toFixed(0) : '-'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                                    <span style={{ fontWeight: 'bold' }}>Payment: </span>{load.paymentMethod || 'N/A'}
                                </Typography>
                            </DetailSection>
                        </Box>

                        <Box sx={{ flex: '1 1 50%' }}>
                            <DetailSection title="Company">
                                <Typography variant="caption" sx={{ display: "block" }}>
                                    <span style={{ fontWeight: 'bold' }}>Company Name: </span>{loadDetails.shipper_company || loadDetails.company || 'N/A'}
                                </Typography>
                                <Typography variant="caption" sx={{ display: "block" }}>
                                    <span style={{ fontWeight: 'bold' }}>Phone: </span>{loadDetails.shipper_phone || loadDetails.phone || 'N/A'}
                                </Typography>
                               
                               {load.posted_by_role === 'broker' && (
    <Typography variant="caption" sx={{ display: "block" }}>
        <span style={{ fontWeight: 'bold' }}>
            {getRegistrationType(load.registration_number)} :
        </span>{" "}
        {load.registration_number || 'N/A'}
    </Typography>
)}
                            </DetailSection>
                          

{assignedCarrier && (
    <Box sx={{ mt: 2 }}>
        <DetailSection title="Assigned Carrier">
            <Typography variant="caption" sx={{ display: "block" }}>
                <span style={{ fontWeight: 'bold' }}>Company Name: </span>
                {assignedCarrier.company_name || 'N/A'}
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
                <span style={{ fontWeight: 'bold' }}>
                    {assignedCarrier.dot_number ? getRegistrationType(assignedCarrier.dot_number) : 'DOT'} Number: 
                </span>
                {assignedCarrier.dot_number || 'N/A'}
            </Typography>
            {assignedCarrier.phone_number && (
                <Typography variant="caption" sx={{ display: "block" }}>
                    <span style={{ fontWeight: 'bold' }}>Phone: </span>
                    {assignedCarrier.phone_number}
                </Typography>
            )}
        </DetailSection>
    </Box>
)}
                        </Box>
                        
                    </Box>

                    <DetailSection title="Notes">
                        <Typography variant="caption">
                            {load.comment || "No additional notes provided."}
                        </Typography>
                    </DetailSection>
                </Box>
            </Box>
        </CardContent>
    </Card>
</Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const LoadHistory = () => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const limit = 50;
    const [selectionMode, setSelectionMode] = useState(false);
    const [selected, setSelected] = useState([]);

  

       const [carriers, setCarriers] = useState([]);

useEffect(() => {
  const fetchCarriers = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/posts/carriers`,
        { withCredentials: true }
      );
      setCarriers(res.data);
    } catch (err) {
      console.error('Failed to load carriers', err);
    }
  };

  fetchCarriers();
}, []);


    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {},
    });

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleAssignAction = async (loadId, carrier) => {
        try {
            const token = localStorage.getItem('jwtToken');
            const API_BASE = process.env.REACT_APP_API_BASE || '';
            await axios.put(
                `${API_BASE}/api/posts/${loadId}/assignCarrier`,
                { carrierId: carrier.id },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            setSnackbar({ open: true, message: '✅ Carrier assigned successfully!', severity: 'success' });
            handleAssignCarrier(loadId, parseInt(carrier.id));
            handleCloseDialog();

        } catch (err) {
            console.error('Failed to assign carrier:', err);
            setSnackbar({
                open: true,
                message: 'Error assigning carrier. Please try again.',
                severity: 'error'
            });
            handleCloseDialog();
        }
    };

    const openAssignDialog = (loadId, carrier) => {
        setConfirmDialog({
            open: true,
            title: 'Confirm Carrier Assignment',
            message: `Are you sure you want to assign this load to "${carrier.company_name}"? You won't be able to change it later.`,
            onConfirm: () => handleAssignAction(loadId, carrier),
            confirmText: 'Assign Carrier'
        });
    };

    const handleAssignCarrier = (loadId, carrierId) => {
        setHistory(prevHistory => 
            prevHistory.map(load => 
                load.id === loadId ? { ...load, carrier_id: carrierId } : load
            )
        );
    };

    const handleCloseDialog = () => {
        setConfirmDialog({ ...confirmDialog, open: false });
    };

    const fetchHistory = async (pageNum) => {
        if (pageNum === 1) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        setError(null);

        try {
            const token = localStorage.getItem('jwtToken');
            if (!token) {
                throw new Error('No authentication token found. Please log in.');
            }

            const API_BASE = process.env.REACT_APP_API_BASE || '';
            const endpoint = `${API_BASE}/api/posts/history?page=${pageNum}&limit=${limit}`;

            const response = await axios.get(endpoint, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const newResults = response.data.results || [];

            setHistory(prev => pageNum === 1 ? newResults : [...prev, ...newResults]);
            setHasMore(newResults.length === limit);
            setPage(pageNum);

        } catch (err) {
            console.error("Failed to fetch history:", err);
            const message = err.response?.data?.message || err.message || 'Failed to fetch load history. Check server connection.';
            setError(message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchHistory(1);
    }, []);

    const handleLoadMore = () => {
        if (hasMore && !loadingMore) {
            fetchHistory(page + 1);
        }
    };

 
    const observer = useRef();
    const lastLoadElementRef = useCallback(node => {
        if (loadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                handleLoadMore();
            }
        });
        if (node) observer.current.observe(node);
    }, [loadingMore, hasMore]);

    const handleEnterSelectionMode = (id) => {
        setSelectionMode(true);
        setSelected([id]);
    };

    const handleSelect = (loadId) => {
        const selectedIndex = selected.indexOf(loadId);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, loadId);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            const newSelecteds = pastLoads.map((l) => l.id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleExitSelectionMode = () => {
        setSelectionMode(false);
        setSelected([]);
    };

    const handleDeleteRequest = (loadIds) => {
        const message = loadIds.length > 1
            ? `Are you sure you want to permanently delete these ${loadIds.length} load records? This action cannot be undone.`
            : 'Are you sure you want to permanently delete this load record? This action cannot be undone.';

        setConfirmDialog({
            open: true,
            title: 'Confirm Deletion',
            message: message,
            onConfirm: () => handleConfirmDelete(loadIds),
            confirmText: 'Confirm Delete'
        });
    };

    const handleConfirmDelete = async (idsToDelete) => {
        if (idsToDelete.length === 0) return;
        try {
            const token = localStorage.getItem('jwtToken');
            const API_BASE = process.env.REACT_APP_API_BASE || '';
            
            const deletePromises = idsToDelete.map(id => 
                axios.delete(`${API_BASE}/api/posts/${id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            );
            await Promise.all(deletePromises);

            setHistory(h => h.filter(l => !idsToDelete.includes(l.id)));
            setSnackbar({ open: true, message: '✅ Records deleted successfully!', severity: 'success' });
        } catch (err) {
            setSnackbar({ open: true, message: 'Error deleting records. Please try again.', severity: 'error' });
            console.error('Bulk delete error:', err);
        } finally {
            handleCloseDialog();
            handleExitSelectionMode();
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
    }

    const pastLoads = history.filter(load => {
        const pickup = load.pickupDate ? dayjs(load.pickupDate) : null;
        return pickup && pickup.isBefore(dayjs(), 'day');
    });


    return (
        <Box sx={{ p: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#11224d' }}>
                    Total Past Loads: {pastLoads.length}
                </Typography>
                {selectionMode ? (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDeleteRequest(selected)}
                            disabled={selected.length === 0}
                        >
                            Delete ({selected.length})
                        </Button>
                        <Button size="small" onClick={handleExitSelectionMode}>Cancel</Button>
                    </Box>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectionMode(true)}
                        disabled={pastLoads.length === 0}
                    >
                        Select
                    </Button>
                )}
            </Box>

           <TableContainer component={StyledPaper} elevation={0} sx={{ maxHeight: 'calc(100vh - 120px)' }}>
  <Table sx={{ minWidth: 850 }} aria-label="load history table" stickyHeader>
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" sx={{ backgroundColor: '#11224d' }}>{selectionMode && <Checkbox sx={{color: 'white'}} indeterminate={selected.length > 0 && selected.length < pastLoads.length} checked={pastLoads.length > 0 && selected.length === pastLoads.length} onChange={handleSelectAll} />}</TableCell>
        {[
          'Pickup Date',
          'Origin',
          'Destination',
          'Equipment',
          'Rate',
          'Covered With',
          'Action'
        ].map((heading) => (
          <TableCell
            key={heading}
            align="center"
            sx={{
              fontWeight: 'bold',
              color: '#ffffff',
              fontSize: '0.7rem',
              py: 0.6,
              textAlign: 'center',
              borderRight: '1px solid rgba(255,255,255,0.1)',
              backgroundColor: '#11224d'
            }}
          >
            {heading}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>

    <TableBody>
      {pastLoads.length > 0 ? <>
        {pastLoads.map((load, idx) => (
          <HistoryRow
            key={load.id}
            load={load} 
            idx={idx} 
            onDelete={handleEnterSelectionMode} 
            carriers={carriers} 
            onOpenAssignDialog={openAssignDialog}
            onAssignCarrier={handleAssignCarrier} 
            selectionMode={selectionMode}
            isSelected={selected.indexOf(load.id) !== -1}
            onSelect={handleSelect}
          />
        ))}
       
        <TableRow ref={lastLoadElementRef}>
          <TableCell colSpan={8} align="center" sx={{ py: 2, border: 'none' }}>
            {loadingMore && <CircularProgress size={24} />}
            {!hasMore && history.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                End of history
              </Typography>
            )}
          </TableCell>
        </TableRow>
      </>
       : (
        <TableRow>
          <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
            <Typography variant="subtitle1" color="text.secondary">
              You have no completed or past loads in your history.
            </Typography>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
</TableContainer>


            <Dialog
                            open={confirmDialog.open}
                            onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
                            aria-labelledby="confirm-dialog-title"
                            aria-describedby="confirm-dialog-description"
                            maxWidth="xs" 
                            fullWidth 
                        >
                            <DialogTitle id="confirm-dialog-title" sx={{ fontWeight: 700 }}>
                                {confirmDialog.title}
                            </DialogTitle>
                            <DialogContent>
                                <DialogContentText id="confirm-dialog-description">
                                    {confirmDialog.message}
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
                                    Cancel
                                </Button>
                                <Button onClick={confirmDialog.onConfirm} color="error" autoFocus>
                                    {confirmDialog.confirmText || 'Confirm Delete'}
                                </Button>
                            </DialogActions>
                        </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default LoadHistory;